import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';

const decodeJwt = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};
import FacebookLogin from 'react-facebook-login/dist/facebook-login-render-props';
import CircularProgress from '@mui/material/CircularProgress';
import { createTheme, ThemeProvider, StyledEngineProvider, adaptV4Theme } from '@mui/material/styles';
import styles from './Start.module.css';
import {
  GENERAL_GRAPH,
  getEditAssumptionsInitialState,
  getUser,
  get_clients,
  google_login,
  facebook_login,
  tokenSelector,
  BLUE_PLOOT,
  does_user_exist,
} from '@plootus/common';

const theme = createTheme(adaptV4Theme({
  palette: {
    primary: { main: BLUE_PLOOT },
  },
  typography: { useNextVariants: true },
}));

const validateEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const Start = ({ closeModal, nextPress, setSignupData, loginopenModal }) => {
  const token = useSelector(tokenSelector);
  const [route, setRoute] = useState(false);
  const [addRoute, setAddRoute] = useState(false);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isEmailTaken, setIsEmailTaken] = useState(false);

  // Track if we are in a signup process to prevent redirects from stale tokens
  const isProcessActive = React.useRef(true);

  // Handle token-based authentication redirects
  React.useEffect(() => {
    (async () => {
      // If a token is detected, it means the user is already logged in (likely from a previous session or a parallel tab)
      // BUT if they just clicked "Create an account" from the login modal, we want to stay here
      // We check if the token is "fresh" by seeing if it was just set in this session
      if (token) {
        // Only redirect if this isn't the first render of the signup screen
        // (meaning the user didn't just open it from the login screen)
        if (!isProcessActive.current) {
          console.log('User already logged in, redirecting...');
          const userData = await dispatch(getUser());
          await localStorage.setItem('jwt_token', token);
          if (userData.userType === 'advisor') {
            await dispatch(get_clients());
            setAddRoute(true);
          } else {
            dispatch({
              type: GENERAL_GRAPH,
              payload: await getEditAssumptionsInitialState(token, userData),
            });
            setRoute(true);
          }
        }
      }
    })();
  }, [token, dispatch]);

  // Set process to inactive after initial mount
  React.useEffect(() => {
    isProcessActive.current = true; // Ensure it's true on mount
    const timer = setTimeout(() => {
      isProcessActive.current = false;
    }, 2000); // Increased buffer to ensure Redux state has settled
    return () => clearTimeout(timer);
  }, []);

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    setEmailError('');
    setIsEmailTaken(false);

    if (newEmail && !validateEmail(newEmail)) {
      setEmailError('Please enter a valid email address');
    }
  };

  const handleEmailSignup = async () => {
    if (!email) {
      setEmailError('Email is required');
      return;
    }

    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      const emailExists = await dispatch(does_user_exist(email));
      if (emailExists) {
        setIsEmailTaken(true);
      } else {
        setSignupData((prevState) => ({
          ...prevState,
          email: email,
          socialLogin: null,
          socialId: null,
        }));
        nextPress();
      }
    } catch (error) {
      setEmailError('Error checking email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = () => {
    closeModal();
    loginopenModal();
  };

  const responseGoogle = async (credentialResponse) => {
    try {
      setLoading(true);
      const decoded = decodeJwt(credentialResponse.credential);
      if (!decoded || !decoded.email) {
        setEmailError('Invalid Google token received.');
        setLoading(false);
        return;
      }
      
      const emailExists = await dispatch(does_user_exist(decoded.email));

      if (emailExists) {
        // If email exists, try to log in with Google credentials
        await dispatch(google_login(credentialResponse.credential));
        setEmail(decoded.email);
        setIsEmailTaken(true);
        return;
      }

      setSignupData((prevState) => ({
        ...prevState,
        email: decoded.email,
        firstName: decoded.given_name || '',
        lastName: decoded.family_name || '',
        socialLogin: 'google',
        socialId: decoded.sub,
      }));
      nextPress();
    } catch (error) {
      console.error('Google login error:', error);
      setEmailError('Google login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const responseFacebook = async (response) => {
    try {
      setLoading(true);
      const { email, name, id } = response;

      // Check if email exists
      if (email) {
        const emailExists = await dispatch(does_user_exist(email));

        if (emailExists) {
          // If email exists, try to log in with Facebook credentials
          await dispatch(facebook_login(response.accessToken));
          setEmail(email);
          setIsEmailTaken(true);
          return;
        }

        // Extract first and last name from full name
        const nameParts = name.split(' ');
        const firstName = nameParts[0] || '';
        const lastName =
          nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';

        setSignupData((prevState) => ({
          ...prevState,
          email: email,
          firstName: firstName,
          lastName: lastName,
          socialLogin: 'facebook',
          socialId: id,
        }));
        nextPress();
      } else {
        setEmailError('Facebook login failed. Email access is required.');
      }
    } catch (error) {
      console.error('Facebook login error:', error);
      setEmailError('Facebook login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (route) return <Navigate to="/auth/401k" replace />;
  if (addRoute) return <Navigate to="/auth/adash" replace />;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Get Started!</h1>
        <button
          onClick={closeModal}
          className={styles.closeButton}
          aria-label="Close"
        >
          <img src="/cross-thin.png" alt="close" className={styles.closeIcon} />
        </button>
      </div>
      <div className={styles.formContainer}>
        <div className={styles.inputGroup}>
          <label className={styles.label}>
            Email ID<span className={styles.required}>*</span>
          </label>
          <input
            type="email"
            value={email}
            onChange={handleEmailChange}
            className={`${styles.input} ${
              emailError || isEmailTaken ? styles.inputError : ''
            }`}
            placeholder="Enter your email"
            disabled={loading}
          />
          {emailError && (
            <span className={styles.errorMessage}>{emailError}</span>
          )}
          {isEmailTaken && (
            <div className={styles.loginPrompt}>
              <span className={styles.errorMessage}>
                This email is already registered.
              </span>
              <button onClick={handleLogin} className={styles.loginLink}>
                Log in instead
              </button>
            </div>
          )}
        </div>

        <button
          onClick={handleEmailSignup}
          className={styles.emailButton}
          disabled={loading || !email || emailError}
        >
          {loading ? (
            <StyledEngineProvider injectFirst>
              (<ThemeProvider theme={theme}>
                <CircularProgress size={24} color="inherit" />
              </ThemeProvider>)
            </StyledEngineProvider>
          ) : (
            'Sign up with Email'
          )}
        </button>

        <div className={styles.divider}>
          <span className={styles.dividerText}>OR</span>
        </div>

        <div className={styles.socialButtons}>
          <GoogleLogin
            onSuccess={responseGoogle}
            onError={() => {
              console.error('Google login failed');
              setEmailError('Google login failed. Please try again.');
            }}
            useOneTap
          />

          {/* <button className={styles.socialButton} disabled={loading}>
            <img src="/appleblack.png" alt="" className={styles.socialIcon} />
            <span>Continue with Apple</span>
          </button> */}
        </div>
      </div>
      {loading && (
        <div className={styles.loadingOverlay}>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
              <CircularProgress size={40} />
            </ThemeProvider>
          </StyledEngineProvider>
        </div>
      )}
    </div>
  );
};

export default Start;
